// Booking vocabulary shared by API and web.

export const SLOTS = Object.freeze([
  { value: 'am', label: 'AM' },
  { value: 'pm', label: 'PM' },
  { value: 'all_day', label: 'All day' },
]);
export const SLOT_LABEL = Object.freeze(Object.fromEntries(SLOTS.map((s) => [s.value, s.label])));

// booked: not yet on a team's day. assigned: has a team and a date (set
// automatically). The rest the office sets until engineers update jobs from
// the app. aborted (engineers on site couldn't install), cancelled and
// no_access need a reason.
export const STATUSES = Object.freeze([
  { value: 'booked', label: 'Booked' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'on_the_way', label: 'On route' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'aborted', label: 'Aborted' },
  { value: 'no_access', label: 'No access' },
  { value: 'cancelled', label: 'Cancelled' },
]);
export const STATUS_LABEL = Object.freeze(Object.fromEntries(STATUSES.map((s) => [s.value, s.label])));
export const STATUS_NEEDS_REASON = Object.freeze(['aborted', 'no_access', 'cancelled', 'paused']);
// What an admin may delete outright: nobody has been out to it yet.
// How far along a job is, for the app's buttons. Booked and Assigned are
// "Not started" to engineers; steps only move forward. A paused job is back
// at the start (On route again when they head back) but keeps what was done.
export const JOB_STEP = Object.freeze({ booked: 0, assigned: 0, paused: 0, on_the_way: 1, in_progress: 2, completed: 3, aborted: 3, no_access: 3, cancelled: 3 });
export function jobStatusLabel(status) {
  if (status === 'paused') return STATUS_LABEL.paused;
  return JOB_STEP[status] === 0 ? 'Not started' : STATUS_LABEL[status];
}
// Why the team pauses a job part way through to come back later. Other
// needs words.
export const PAUSE_REASONS = Object.freeze([
  'Waiting for the waste tipper',
  'Waiting for materials',
  'Letting reinstatement set',
  'Supervisor asked us to move',
  'Other',
]);
// Why an engineer does a different job next than planned.
export const SWITCH_REASONS = Object.freeze(['Customer not home', 'No access yet', 'Closer job', 'Other']);
// Going back to a paused job needs no other reason.
export const RESUME_REASON = 'Back to a paused job';
// Why engineers on site can't install. Some leave a chance later the same
// day (circle back: the job goes to the end of the day, once); the rest can
// only be aborted. Other needs words.
export const ABORT_REASONS = Object.freeze([
  { value: 'Customer not home', circleBack: true },
  { value: 'Access blocked', circleBack: true },
  { value: 'Customer refused', circleBack: false },
  { value: 'Unsafe to work', circleBack: false },
  { value: 'Meter position or pipe not suitable', circleBack: false },
  { value: 'Needs other work first', circleBack: false },
  { value: 'Other', circleBack: true },
]);
export const MAX_CIRCLE_BACKS = 1;

// The job a team is on now, from its jobs in planned order: the one On route
// or In progress; else the one they chose to do next; else the next one not
// started, leaving jobs they set aside until last (same-day circle back).
export function currentJob(jobs, chosenId = null) {
  const active = jobs.find((j) => JOB_STEP[j.status] === 1 || JOB_STEP[j.status] === 2);
  if (active) return active;
  const open = jobs.filter((j) => JOB_STEP[j.status] === 0);
  return open.find((j) => j.id === chosenId) || open.find((j) => !j.skippedAt) || open[0] || null;
}
export const STATUS_DELETABLE = Object.freeze(['booked', 'assigned', 'cancelled']);
// Tasks (each person's own to-do list): how urgent one is.
export const TASK_PRIORITIES = Object.freeze([
  { value: 'none', label: 'No priority' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]);
// Who told us a finished install has stopped working (a defect).
export const DEFECT_REPORTERS = Object.freeze([
  { value: 'customer', label: 'Customer' },
  { value: 'network_plus', label: 'Network Plus' },
  { value: 'our_team', label: 'Our team' },
  { value: 'other', label: 'Other' },
]);

// NU-000123 from the booking's id.
export function bookingReference(id) {
  return `NU-${String(id).padStart(6, '0')}`;
}

// Incidents engineers report on a job. Utility strikes say which service.
// The supervisor decides what happens next (they are alerted at once).
export const INCIDENT_TYPES = Object.freeze([
  'Utility strike',
  'Damage to property or vehicle',
  'Injury',
  'Near miss',
  'Vehicle accident',
  'Other',
]);
export const STRIKE_SERVICES = Object.freeze(['Gas', 'Water', 'Electric', 'Telecoms', 'Not sure']);
// INC-000012 from the incident's number.
export function incidentReference(n) {
  return `INC-${String(n).padStart(6, '0')}`;
}

// Support the team can ask for during the works: their supervisor on site
// (with a reason), or a waste tipper. The supervisor and operations are
// alerted at once.
export const SUPPORT_TYPES = Object.freeze(['Supervisor', 'Waste tipper']);
// SUP-000004 from the request's number.
export function supportReference(n) {
  return `SUP-${String(n).padStart(6, '0')}`;
}
